import { useState, useEffect } from 'react'
import { Card, Typography, Spin } from 'antd'
import * as api from '../lib/api'
import { useI18n } from '../i18n/I18nProvider'
import Title from 'antd/es/typography/Title'

const { Text } = Typography

const BG_COLORS = ['#dce8f6', '#d6ebd9', '#d9eedc', '#f5e6c8']

function toNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

export default function RatingDistributionChart({ sessionId = null, scope = null, data: providedData = null }) {
  const { t } = useI18n()
  const [fetchedData, setFetchedData] = useState(null)
  const [loading, setLoading] = useState(!providedData)

  useEffect(() => {
    if (providedData) {
      setLoading(false)
      return
    }
    setLoading(true)
    api.getRatingDistribution(sessionId, scope)
      .then(setFetchedData)
      .catch(() => { })
      .finally(() => setLoading(false))
  }, [providedData, scope, sessionId])

  if (loading) return <Card size='small' style={{ marginBottom: 20, textAlign: 'center' }}><Spin /></Card>
  const data = providedData || fetchedData
  if (!data) return null

  const categories = Array.isArray(data.categories) ? data.categories : []
  if (!categories.length) return null
  const headcountTotal = data.headcount_total ?? data.total ?? 0
  const ratedTotal = data.rated_total ?? data.total ?? 0

  const formatPct = (value) => `${toNumber(value).toFixed(1)}%`

  // Build points for the expected curve (smooth bell)
  const expectedVals = categories.map(category => toNumber(category.expected))
  const actualVals = categories.map(category => toNumber(category.actual))
  const maxVal = Math.max(...expectedVals, ...actualVals, 10)

  // SVG dimensions
  const W = 820
  const H = 240
  const PAD_LEFT = 10
  const PAD_RIGHT = 10
  const PAD_TOP = 30
  const PAD_BOTTOM = 62
  const chartW = W - PAD_LEFT - PAD_RIGHT
  const chartH = H - PAD_TOP - PAD_BOTTOM
  const segW = chartW / categories.length

  // Helper: x center of each category
  const cx = (i) => PAD_LEFT + segW * i + segW / 2
  const cy = (val) => PAD_TOP + chartH - (val / maxVal) * chartH

  // Smooth curve through points
  const makeCurve = (vals) => {
    const points = vals.map((v, i) => [cx(i), cy(v)])
    if (points.length < 2) return ''
    let d = `M${points[0][0]},${points[0][1]}`
    for (let i = 0; i < points.length - 1; i++) {
      const cp1x = points[i][0] + segW / 3
      const cp1y = points[i][1]
      const cp2x = points[i + 1][0] - segW / 3
      const cp2y = points[i + 1][1]
      d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${points[i + 1][0]},${points[i + 1][1]}`
    }
    return d
  }

  return (
    <Card size='small' style={{ marginBottom: 20 }}>
      <div style={{ marginBottom: 8 }}>
        <Title level={4}>{t('ratings.distribution.title', 'Performance Rating Distribution:')} </Title>
        <Text>{t('ratings.distribution.expected', 'Expected')}</Text>
        <Text strong> {t('ratings.distribution.vs', 'vs')} </Text>
        <Text style={{ color: 'var(--tti-info)' }}>{t('ratings.distribution.actual', 'Actual')}</Text>
        <Text type='secondary' style={{ marginLeft: 16, fontSize: 12 }}>
          ({t('ratings.distribution.headcount', '{{rated}} rated / {{total}} total headcount', { rated: ratedTotal, total: headcountTotal })})
        </Text>
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 540px' }}>
          <svg width='100%' viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }}>
            {/* Background segments */}
            {categories.map((category, i) => (
              <rect
                key={category.code}
                x={PAD_LEFT + segW * i}
                y={PAD_TOP}
                width={segW}
                height={chartH}
                fill={BG_COLORS[i % BG_COLORS.length]}
                opacity={0.5}
              />
            ))}

            {/* Expected curve */}
            <path d={makeCurve(expectedVals)} fill='none' stroke='#bbb' strokeWidth={2.5} strokeDasharray='6,3' />
            {expectedVals.map((v, i) => (
              <g key={`exp-${i}`}>
                <circle cx={cx(i)} cy={cy(v)} r={4} fill='#bbb' />
                <text x={cx(i)} y={cy(v) - 10} textAnchor='middle' fontSize={10} fill='var(--tti-muted)'>{formatPct(v)}</text>
              </g>
            ))}

            {/* Actual curve */}
            <path d={makeCurve(actualVals)} fill='none' stroke='var(--tti-info)' strokeWidth={2.5} />
            {actualVals.map((v, i) => (
              <g key={`act-${i}`}>
                <circle cx={cx(i)} cy={cy(v)} r={5} fill='var(--tti-info)' />
                <rect x={cx(i) - 18} y={cy(v) + 6} width={36} height={18} rx={3} fill='var(--tti-info)' />
                <text x={cx(i)} y={cy(v) + 19} textAnchor='middle' fontSize={10} fill='var(--neutral-white)' fontWeight='bold'>{formatPct(v)}</text>
              </g>
            ))}

            {/* Category labels */}
            {categories.map((category, i) => (
              <text
                key={category.code}
                x={cx(i)}
                y={H - 10}
                textAnchor='middle'
                fontSize={10}
                fill='#595959'
              >
                {category.code}
              </text>
            ))}
          </svg>
        </div>

        <div style={{ flex: '0 0 240px', width: 240 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #f0f0f0', padding: '6px 4px' }}>{t('ratings.distribution.grade', 'Grade')}</th>
                <th style={{ textAlign: 'right', borderBottom: '1px solid #f0f0f0', padding: '6px 4px' }}>{t('ratings.distribution.expected', 'Expected')}</th>
                <th style={{ textAlign: 'right', borderBottom: '1px solid #f0f0f0', padding: '6px 4px' }}>{t('ratings.distribution.actual', 'Actual')}</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(category => (
                <tr key={category.code}>
                  <td style={{ padding: '6px 4px', borderBottom: '1px solid #fafafa' }}><Text strong title={category.label}>{category.code}</Text></td>
                  <td style={{ padding: '6px 4px', borderBottom: '1px solid #fafafa', textAlign: 'right' }}>{formatPct(category.expected)}</td>
                  <td style={{ padding: '6px 4px', borderBottom: '1px solid #fafafa', textAlign: 'right' }}>{formatPct(category.actual)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  )
}